import { type ComponentProps } from 'react';
import { createRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Action, Subject } from '@erp/authz';
import { BookOpenIcon } from '@phosphor-icons/react';
import { msg, T, useGT } from 'gt-react';
import { z } from 'zod';
import Page from '@/components/page/Page';
import { orpc } from '@/api/rpc/client';
import { useCan } from '@/hooks/useCan';
import { useNamespaces } from '@/api/rpc/hooks/namespaces/useNamespaces';
import {
  formatNamespaceLabel,
  unassignedNamespaceOption,
} from '@/components/namespaces/namespaceLabels';
import { MultiSelect } from '@/components/common/MultiSelect';
import { Route as parent, requirePermission } from './route';
import { WorkbookCard } from './workbooks/WorkbookCard';
import { WorkbookCreateButton } from './workbooks/WorkbookCreateButton';
import {
  workbookNamespaceGroups,
  type WorkbookNamespaceGroup,
} from './workbooks/workbookNamespaceGroups';

type WorkbookSummary = ComponentProps<typeof WorkbookCard>['workbook'] & {
  namespaceId: string | null;
};
function WorkbooksPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const [canDelete] = useCan(Action.Delete, Subject.Workbook);
  const list = useQuery(orpc.workbooks.list.queryOptions({ input: {} }));
  const { data: namespaces = [] } = useNamespaces();
  const workbooks = list.data ?? [];
  const filter = search.namespaces ?? [];
  const groups = workbookNamespaceGroups(workbooks, namespaces, filter);
  return (
    <Page
      title={<T context="workbooks title">Workbooks</T>}
      subtitle={
        <T context="workbooks subtitle">
          Turn reference files and business data into repeatable worksheets.
        </T>
      }
      actions={<WorkbookCreateButton />}
      isLoading={list.isPending}
      error={list.error ?? undefined}
    >
      {namespaces.length > 0 && workbooks.length > 0 && (
        <div className="my-3">
          <WorkbookNamespaceFilter
            namespaces={namespaces}
            filter={filter}
            onChange={(selected) =>
              navigate({
                to: '/workbooks',
                search: {
                  namespaces: selected.length > 0 ? selected : undefined,
                },
                replace: true,
              })
            }
          />
        </div>
      )}
      <WorkbookGroupList groups={groups} canDelete={canDelete} />
      {list.data && (
        <WorkbookListEmpty
          workbookCount={list.data.length}
          groupCount={groups.length}
        />
      )}
    </Page>
  );
}

/** Narrows the list to some namespaces (and the ungrouped Workbooks). */
function WorkbookNamespaceFilter({
  namespaces,
  filter,
  onChange,
}: {
  namespaces: readonly { id: string; name: string; key: string }[];
  filter: readonly string[];
  onChange: (namespaceIds: string[]) => void;
}) {
  const gt = useGT();
  const unassigned = unassignedNamespaceOption(gt);
  const filterOptions = [
    { id: unassigned.id, label: unassigned.label },
    ...namespaces.map((entry) => ({
      id: entry.id,
      label: formatNamespaceLabel(gt, entry),
    })),
  ];
  return (
    <div className="w-80 max-w-full">
      <MultiSelect
        options={filterOptions}
        value={filterOptions.filter((option) => filter.includes(option.id))}
        onChange={(selected) => onChange(selected.map((option) => option.id))}
        placeholder={gt('Namespace', {
          context: 'workbooks list namespace filter',
        })}
        showPlaceholderInSelectionText
        liveUpdate
        size="sm"
      />
    </div>
  );
}

/** The Workbooks, under a heading per namespace once any has one. */
function WorkbookGroupList({
  groups,
  canDelete,
}: {
  groups: WorkbookNamespaceGroup<WorkbookSummary>[];
  canDelete: boolean;
}) {
  const gt = useGT();
  const grouped = groups.some((group) => group.namespace);
  return (
    <>
      {groups.map((group) => (
        <section key={group.namespace?.id ?? 'ungrouped'} className="mb-2">
          {grouped && (
            <h2 className="mb-2 text-sm font-medium text-muted-foreground">
              {group.namespace ? (
                formatNamespaceLabel(gt, group.namespace)
              ) : (
                <T context="workbooks list heading for ungrouped workbooks">
                  No namespace
                </T>
              )}
            </h2>
          )}
          <div className="grid gap-3 md:grid-cols-3">
            {group.workbooks.map((workbook) => (
              <WorkbookCard
                key={workbook.id}
                workbook={workbook}
                canDelete={canDelete}
              />
            ))}
          </div>
        </section>
      ))}
    </>
  );
}

/** Why the list shows no Workbook: there are none, or the filter hides them all. */
function WorkbookListEmpty({
  workbookCount,
  groupCount,
}: {
  workbookCount: number;
  groupCount: number;
}) {
  if (workbookCount === 0)
    return (
      <p className="py-8 text-muted-foreground">
        <T context="empty workbooks">
          Create a workbook, attach an xlsx reference, and describe the process
          you want the assistant to automate.
        </T>
      </p>
    );
  if (groupCount > 0) return null;
  return (
    <p className="py-8 text-muted-foreground">
      <T context="workbooks list empty after namespace filter">
        No workbooks in the selected namespaces.
      </T>
    </p>
  );
}

export const Route = createRoute({
  getParentRoute: () => parent,
  path: '/workbooks',
  component: WorkbooksPage,
  validateSearch: z.object({ namespaces: z.array(z.string()).optional() }),
  staticData: {
    app: {
      nav: {
        label: msg('Workbooks', { context: 'sidebar nav label' }),
        icon: <BookOpenIcon className="size-4" />,
        group: 'Overview',
        order: 8,
        groupOrder: 1,
      },
      access: {
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Workbook }],
      },
    },
  },
  loader: () => ({
    crumb: msg('Workbooks', { context: 'workbooks breadcrumb' }),
  }),
  beforeLoad: async ({ context }) =>
    requirePermission({
      context,
      action: Action.Read,
      subject: Subject.Workbook,
    }),
});
